import { useState, FormEvent } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const PHONE = '03-3261-9551';
const FAX   = '050-6865-3317';
const EMAIL = 'info@s-a-security.com';

const contactMethods = [
  {
    icon: 'ri-phone-line',
    label: 'お電話',
    value: PHONE,
    note: '平日 8:00〜18:00',
    badge: 'TEL',
    action: { type: 'tel', href: `tel:${PHONE}`, text: '電話をかける' },
    bg: 'bg-[#0D1A4E]',
    valueCls: 'text-[#38BDF8] text-xl font-black tracking-wider',
  },
  {
    icon: 'ri-mail-line',
    label: 'メール',
    value: EMAIL,
    note: '24時間受付・2営業日以内に返信',
    badge: 'MAIL',
    action: { type: 'scroll', href: '#form', text: '↓ フォームへ' },
    bg: 'bg-white',
    valueCls: 'text-[#0D1A4E] text-base font-bold break-all',
  },
  {
    icon: 'ri-printer-line',
    label: 'FAX',
    value: FAX,
    note: '24時間受付',
    badge: 'FAX',
    action: { type: 'copy', href: '', text: '番号をコピー' },
    bg: 'bg-white',
    valueCls: 'text-[#0D1A4E] text-xl font-black tracking-wider',
  },
];

export default function ContactForm() {
  const { ref, isVisible } = useScrollAnimation();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [copied, setCopied] = useState(false);

  const handleCopyFax = () => {
    navigator.clipboard.writeText(FAX).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    const form = e.currentTarget;
    const params = new URLSearchParams();
    new FormData(form).forEach((v, k) => params.append(k, v.toString()));
    try {
      const res = await fetch('https://readdy.ai/api/form/d71mgark1jkj467r5j50', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) { setSubmitted(true); form.reset(); setCharCount(0); }
      else setError('送信に失敗しました。もう一度お試しください。');
    } catch {
      setError('通信エラーが発生しました。もう一度お試しください。');
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = () => {
    const el = document.getElementById('inquiry-form-area');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div ref={ref} className={`fade-in-up ${isVisible ? 'visible' : ''}`}>

          {/* ヘッダー */}
          <div className="text-center mb-12">
            <span className="text-[#38BDF8] text-sm font-medium tracking-widest">CONTACT</span>
            <h2 className="text-[#0D1A4E] text-3xl md:text-4xl font-black mt-3 mb-4">お仕事のご依頼・お問い合わせ</h2>
            <div className="w-12 h-1 bg-[#38BDF8] mx-auto"></div>
            <p className="text-gray-500 mt-6 text-sm leading-relaxed">
              電話・メール・FAXでご連絡ください。<br />
              依頼件数が急拡大中の今だからこそ、スムーズかつスピーディーにご対応いたします。
            </p>
          </div>

          {/* 3本立て連絡先カード */}
          <div className="grid md:grid-cols-3 gap-5 mb-12">
            {contactMethods.map((m) => (
              <div
                key={m.badge}
                className={`${m.bg} border border-gray-100 rounded-lg p-6 flex flex-col gap-3`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-md ${m.bg === 'bg-[#0D1A4E]' ? 'bg-[#38BDF8]/20' : 'bg-[#38BDF8]/10'}`}>
                      <i className={`${m.icon} text-[#38BDF8] text-base`}></i>
                    </div>
                    <span className={`text-xs font-bold ${m.bg === 'bg-[#0D1A4E]' ? 'text-white/60' : 'text-gray-400'}`}>{m.label}</span>
                  </div>
                  <span className={`text-xs font-black px-2 py-0.5 rounded border ${m.bg === 'bg-[#0D1A4E]' ? 'border-[#38BDF8]/40 text-[#38BDF8]' : 'border-[#38BDF8]/30 text-[#38BDF8]'}`}>
                    {m.badge}
                  </span>
                </div>

                <p className={m.valueCls}>{m.value}</p>
                <p className={`text-xs ${m.bg === 'bg-[#0D1A4E]' ? 'text-white/50' : 'text-gray-400'}`}>{m.note}</p>

                {m.action.type === 'tel' && (
                  <a
                    href={m.action.href}
                    className="mt-auto bg-[#38BDF8] hover:bg-[#7dd3fc] text-[#0D1A4E] text-xs font-bold py-2.5 rounded-md text-center whitespace-nowrap transition-colors cursor-pointer"
                  >
                    <i className="ri-phone-fill mr-1.5"></i>{m.action.text}
                  </a>
                )}
                {m.action.type === 'scroll' && (
                  <button
                    onClick={scrollToForm}
                    className="mt-auto border border-[#38BDF8]/40 hover:bg-[#38BDF8]/10 text-[#38BDF8] text-xs font-bold py-2.5 rounded-md text-center whitespace-nowrap transition-colors cursor-pointer"
                  >
                    <i className="ri-edit-line mr-1.5"></i>{m.action.text}
                  </button>
                )}
                {m.action.type === 'copy' && (
                  <button
                    onClick={handleCopyFax}
                    className="mt-auto border border-[#38BDF8]/40 hover:bg-[#38BDF8]/10 text-[#38BDF8] text-xs font-bold py-2.5 rounded-md text-center whitespace-nowrap transition-colors cursor-pointer"
                  >
                    <i className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'} mr-1.5`}></i>
                    {copied ? 'コピーしました' : m.action.text}
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* メール問い合わせフォーム */}
          <div id="inquiry-form-area">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 flex items-center justify-center bg-[#38BDF8]/10 rounded-md">
                <i className="ri-mail-send-line text-[#38BDF8]"></i>
              </div>
              <h3 className="text-[#0D1A4E] font-black text-lg">メールでのお問い合わせ（見積り依頼）</h3>
            </div>

            {submitted ? (
              <div className="border border-green-200 bg-green-50 rounded-lg p-10 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                  <i className="ri-checkbox-circle-line text-green-600 text-3xl"></i>
                </div>
                <h3 className="text-[#0D1A4E] font-bold text-xl mb-2">送信が完了しました</h3>
                <p className="text-gray-500 text-sm">担当者より2営業日以内にご連絡いたします。</p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 text-sm text-[#38BDF8] hover:underline cursor-pointer whitespace-nowrap"
                >
                  続けて問い合わせる
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="job-inquiry-form"
                onSubmit={handleSubmit}
                className="border border-gray-100 rounded-lg p-8 bg-white"
              >
                <div className="mb-5">
                  <label className="block text-[#0D1A4E] text-sm font-bold mb-1.5">
                    社名・法人名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="社名"
                    required
                    placeholder="〇〇建設株式会社"
                    className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#38BDF8] transition-colors"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block text-[#0D1A4E] text-sm font-bold mb-1.5">
                      担当者名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="担当者名"
                      required
                      placeholder="山田 太郎"
                      className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#38BDF8] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-[#0D1A4E] text-sm font-bold mb-1.5">
                      電話番号 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="電話番号"
                      required
                      placeholder="03-0000-0000"
                      className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#38BDF8] transition-colors"
                    />
                  </div>
                </div>

                <div className="mb-5">
                  <label className="block text-[#0D1A4E] text-sm font-bold mb-1.5">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="example@company.co.jp"
                    className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#38BDF8] transition-colors"
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-[#0D1A4E] text-sm font-bold mb-1.5">
                    依頼内容 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="依頼内容"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="作業内容、現場の場所、希望日程などをお気軽にご記入ください。"
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#38BDF8] transition-colors resize-none"
                  />
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-gray-400">最大500文字</span>
                    <span className={`text-xs ${charCount > 480 ? 'text-red-500' : 'text-gray-400'}`}>
                      {charCount}/500
                    </span>
                  </div>
                </div>

                {error && (
                  <div className="mb-4 border border-red-200 bg-red-50 rounded-md px-4 py-3 text-red-600 text-sm">
                    <i className="ri-error-warning-line mr-2"></i>{error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#0D1A4E] hover:bg-[#162266] disabled:bg-gray-400 text-white py-4 rounded-md font-bold whitespace-nowrap transition-colors cursor-pointer text-sm"
                >
                  {submitting ? (
                    <span><i className="ri-loader-4-line animate-spin mr-2"></i>送信中...</span>
                  ) : (
                    <span><i className="ri-file-list-3-line mr-2"></i>見積りを依頼する</span>
                  )}
                </button>

                <p className="text-xs text-gray-400 mt-4 text-center">
                  ご入力いただいた情報は、お問い合わせ対応のみに使用いたします。
                </p>
              </form>
            )}
          </div>

        </div>
      </div>
    </section>
  );
}
